import { useState, useEffect, useRef } from 'react';
import { Save, Network, Settings, Shield, Loader2, ChevronDown, ChevronUp, Plus, Edit, Trash2, Server, Globe, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import api from '../../services/api';
import edgeService from '../../services/edge.service';
import DHCPPoolModal from './subscription/DHCPPoolModal';
import SourceNATModal from './subscription/SourceNATModal';
import PortForwardingModal from './subscription/PortForwardingModal';
import { usePermissions } from '../../hooks/usePermissions';
import { apiErrorMessage } from '../../utils/apiError';

interface VRFOption {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  vrfId?: number;
}

interface LogicalInterfaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Return the created interface id when adding a new one, so the modal can
   * transition in-place from add→edit and unlock the subscription sections.
   * Updates (mode='edit') may return void.
   */
  onSave: (data: any) => Promise<{ id?: string } | void>;
  /**
   * Atomic create. In add mode the modal collects the interface plus any
   * drafted subscriptions and commits them in one call. The parent forwards
   * this to `edgeService.commitLogicalInterface` and refreshes its list. Only
   * used for `mode === 'add'`.
   */
  onCommit?: (payload: { interface: any; subscriptions: any[] }) => Promise<void>;
  interface?: any;
  mode: 'add' | 'edit';
  physicalInterfaces: Array<{ name: string; id: string; configuredState?: boolean }>;
  edgeId?: string;
  // VSNP-690: sibling logical interfaces on the same edge — used to detect
  // duplicate VRF assignment across multiple WAN-role interfaces and prompt
  // the operator at save time.
  siblingInterfaces?: Array<{ id: string; role?: string; vrfId?: string | null; name?: string }>;
}

/**
 * Logical Interface modal:
 *   - Tabless single form with context-sensitive sections
 *   - Role+Type drive which sections render; forbidden combinations blocked inline
 *   - DHCP Server / Source NAT / Port Forwarding sections embed subscription
 *     management directly (mirrors Rules-in-Routing-Policy pattern)
 *   - Adding a NEW interface, subscriptions are drafted client-side and the
 *     whole thing commits atomically in one Save (onCommit). Editing an
 *     existing interface persists the interface and its subscriptions directly.
 */
const LogicalInterfaceModal = ({
  isOpen,
  onClose,
  onSave,
  onCommit,
  interface: iface,
  mode,
  physicalInterfaces,
  edgeId,
  siblingInterfaces = [],
}: LogicalInterfaceModalProps) => {
  const toast = useToast();
  const { canCreate } = usePermissions();

  // Natural alphanumeric sort (G0, G1, …, G10) — matches PhysicalInterfacesConfig
  // so the dropdown order is consistent with the Physical Interfaces table.
  const sortedPhysicalInterfaces = [...physicalInterfaces].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }),
  );

  // VRF options state
  const [vrfOptions, setVrfOptions] = useState<VRFOption[]>([
    { id: '', name: 'Default', isDefault: true, vrfId: 0 },
  ]);
  const [vrfLoading, setVrfLoading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // VSNP-690: inline VRF creation. Operators need to be able to create new
  // VRFs (with their own VPP FIB table id) directly from the interface
  // editor — without it the dropdown only ever shows "Default (0)" and
  // multi-WAN scenarios are unconfigurable.
  const [newVrfFormOpen, setNewVrfFormOpen] = useState(false);
  const [newVrfName, setNewVrfName] = useState('');
  const [newVrfTableId, setNewVrfTableId] = useState<number>(100);
  const [newVrfDescription, setNewVrfDescription] = useState('');
  const [newVrfSaving, setNewVrfSaving] = useState(false);

  // Suggest the next unused VRF table id when the inline form opens.
  const suggestNextVrfTableId = (existing: VRFOption[]): number => {
    const used = new Set(existing.map((v) => v.vrfId).filter((n): n is number => typeof n === 'number'));
    for (let n = 100; n <= 65535; n++) if (!used.has(n)) return n;
    return 100;
  };

  // The interface id when it exists server-side (edit mode). In add mode it's
  // undefined until the atomic commit creates it.
  const effectiveInterfaceId = iface?.id;
  const hasPersistedInterface = !!effectiveInterfaceId;
  const [saving, setSaving] = useState(false);

  // Add mode: subscriptions are drafted client-side (not persisted) and
  // committed atomically alongside the interface on a single Save. Each draft
  // carries a local `_draftId` for list keys / edit / remove; it's stripped
  // before the commit payload is sent.
  const [draftSubscriptions, setDraftSubscriptions] = useState<any[]>([]);
  const draftIdCounter = useRef(0);

  const [formData, setFormData] = useState({
    name: '',
    physicalInterface: '',
    /**
     * VSNP-659: full canonical list of physical-port IDs this logical
     * interface owns. For Bridged LAN this can be 2+ — every entry
     * becomes an L2 member of the same VPP bridge-domain. For all
     * other interfaces this is a 1-element array (= [physicalInterface]).
     */
    physicalInterfaceIds: [] as string[],
    role: 'LAN',
    type: 'Routed',
    description: '',
    ipv4: '',
    /**
     * VSNP-691 + VSNP-713: alias addresses on the same interface. Each
     * entry is { cidr, gateway? }. Same-subnet aliases (those whose CIDR
     * overlaps the primary's network) leave `gateway` empty — the
     * primary's default route handles their egress. Aliases on a
     * different subnet require `gateway`; the agent installs a
     * source-policy route through that gateway.
     */
    ipv4Aliases: [] as Array<{ cidr: string; gateway?: string }>,
    subnet: '255.255.255.0',
    dhcpClient: false,
    gateway: '',
    mtu: 1500,
    mss: '',
    vrf: '',
    vlanId: '',
  });


  // VSNP-690: only re-initialise the form when the modal actually opens or
  // the iface identity changes — NOT on every parent re-render. Previously
  // the deps included `iface` (object reference) and `physicalInterfaces`
  // (a fresh array on every parent render), so an external poll/refetch
  // mid-edit would clobber the in-progress form state. Track the last
  // (open, iface.id) we initialised for and skip if unchanged.
  const lastInitKey = (() => {
    if (!isOpen) return '__closed__';
    return `${iface?.id || '__add__'}`;
  })();
  const initKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset modal-only ephemeral state on close.
      setDraftSubscriptions([]);
      draftIdCounter.current = 0;
      setAdvancedOpen(false);
      setSubDeleteConfirm(null);
      setSubDeleting(false);
      initKeyRef.current = null;
      return;
    }
    if (initKeyRef.current === lastInitKey) {
      return;
    }
    initKeyRef.current = lastInitKey;

    if (!iface) {
      // Fresh add
      setFormData({
        name: '',
        physicalInterface: '',
        physicalInterfaceIds: [],
        role: 'LAN',
        type: 'Routed',
        description: '',
        ipv4: '',
        ipv4Aliases: [],
        subnet: '255.255.255.0',
        dhcpClient: false,
        gateway: '',
        mtu: 1500,
        mss: '',
        vrf: '',
        vlanId: '',
      });
      return;
    }

    // VSNP-659: hydrate the canonical id list. The API returns
    // `physicalInterfaceNames: string[]` (one per member) — match each name
    // back to its dev_port id. Falls back to splitting `physicalInterface`
    // on commas for older API responses (= the comma-joined name).
    const memberNames: string[] = Array.isArray((iface as any).physicalInterfaceNames)
      ? (iface as any).physicalInterfaceNames
      : (typeof iface.physicalInterface === 'string' && iface.physicalInterface !== '-'
          ? iface.physicalInterface.split(',').map((s: string) => s.trim()).filter(Boolean)
          : []);
    const memberIds = memberNames
      .map((n) => physicalInterfaces.find((pi) => pi.name === n)?.id || '')
      .filter(Boolean);

    const hydrated = {
      name: iface.name === '-' ? '' : (iface.name || ''),
      physicalInterface: memberIds[0] || '',
      physicalInterfaceIds: memberIds,
      role: iface.role || 'LAN',
      type: iface.type || 'Routed',
      description: iface.description === '-' ? '' : (iface.description || ''),
      ipv4: iface.ipv4 === '-' ? '' : (iface.ipv4 || ''),
      // VSNP-713: API may return aliases in either the legacy string
      // form (one CIDR per entry) or the new object form ({cidr, gateway?}).
      // Normalise to the object form here.
      ipv4Aliases: Array.isArray(iface.ipv4Aliases)
        ? iface.ipv4Aliases.map((a: any) =>
            typeof a === 'string'
              ? { cidr: a }
              : { cidr: a?.cidr || '', gateway: a?.gateway || undefined },
          )
        : [],
      subnet: iface.subnet || '255.255.255.0',
      dhcpClient: !!iface.dhcpClient,
      gateway: iface.gateway === '-' ? '' : (iface.gateway || ''),
      mtu: iface.mtu || 1500,
      mss: iface.mss || '',
      vrf: iface.vrfId || '',
      vlanId: iface.vlanId || '',
    };
    setFormData(hydrated);
  }, [isOpen, lastInitKey, iface, physicalInterfaces]);

  // Fetch VRF options when modal opens
  useEffect(() => {
    const fetchVRFs = async () => {
      if (!isOpen) return;
      setVrfLoading(true);
      try {
        // VSNP-690: option `id` MUST be the VRF UUID — that's what
        // dev_logical_port.vrf_id stores and what the form posts back.
        // The numeric `vrfId` (VPP FIB table id) is shown in the label
        // for human reference but is not the FK.
        if (edgeId) {
          const response = await api.get(`/api/v2/edges/${edgeId}/vrf`);
          if (response.data?.items && response.data.items.length > 0) {
            setVrfOptions(response.data.items.map((v: any) => ({
              id: v.id,
              name: v.name,
              description: v.label || v.description,
              vrfId: v.vrfId,
              isDefault: !!v.isDefault || v.vrfId === 0,
            })));
            return;
          }
        }
        const response = await api.get('/api/v2/vrfs');
        if (response.data?.items) {
          setVrfOptions(response.data.items.map((v: any) => ({
            id: v.id,
            name: v.name,
            description: v.description,
            vrfId: v.vrfId,
            isDefault: v.isDefault,
          })));
        }
      } catch (error) {
        console.error('Failed to fetch VRFs:', error);
        setVrfOptions([{ id: '', name: 'Default', isDefault: true, vrfId: 0 }]);
      } finally {
        setVrfLoading(false);
      }
    };
    fetchVRFs();
  }, [isOpen, edgeId]);

  // Subscription state + sub-modal control
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [showDHCPModal, setShowDHCPModal] = useState(false);
  const [showSNATModal, setShowSNATModal] = useState(false);
  const [showPFModal, setShowPFModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<any>(null);
  // Platform confirm dialog for deleting a saved subscription (replaces the
  // native window.confirm so it matches the rest of the UI).
  const [subDeleteConfirm, setSubDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [subDeleting, setSubDeleting] = useState(false);

  const loadSubscriptions = async () => {
    if (!edgeId || !effectiveInterfaceId) return;
    setSubsLoading(true);
    try {
      const resp = await edgeService.listSubscriptions(edgeId, { logicalPortId: effectiveInterfaceId });
      setSubscriptions(resp.items || resp || []);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setSubsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) { setSubscriptions([]); return; }
    if (effectiveInterfaceId && edgeId) loadSubscriptions();
  }, [isOpen, effectiveInterfaceId, edgeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    field: string,
    value: string | boolean | number | string[] | Array<{ cidr: string; gateway?: string }>,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Compatibility guards (mirror the server-side Zod .refine)
  const isBridgedWAN = formData.role === 'WAN' && formData.type === 'Bridged';
  // A bridged LAN acting as a DHCP server can't simultaneously be a DHCP client.
  const showDhcpClient = !(formData.role === 'LAN' && formData.type === 'Bridged');
  // On bridged LAN the static IP fields are always editable — they define the
  // bridge's SVI address used by DHCP pools on top.
  const addressingDisabled = formData.dhcpClient && showDhcpClient;

  // Re-role safety: if an existing interface has attached subscriptions that
  // would be orphaned by the current role choice, flag them. DHCP pools only
  // make sense on LAN; Source NAT / Port Forwarding only on WAN. We soft-warn
  // (allow save) so the user can choose to remove them afterwards — matches
  // the dependency-warning pattern used on delete.
  const originalRole = iface?.role as string | undefined;
  const roleChanged = !!originalRole && originalRole !== formData.role;
  const incompatibleSubs = subscriptions.filter((s) => {
    const t = s.subscription_type;
    if (formData.role === 'WAN' && t === 'dhcp_pool') return true;
    if (formData.role === 'LAN' && (t === 'source_nat' || t === 'port_forwarding')) return true;
    return false;
  });

  // Clear dhcp_client when switching to bridged LAN (forbidden combo)
  useEffect(() => {
    if (!showDhcpClient && formData.dhcpClient) {
      setFormData((p) => ({ ...p, dhcpClient: false }));
    }
  }, [showDhcpClient, formData.dhcpClient]);

  // VSNP-691: dotted-decimal subnet mask → CIDR prefix length. Used to
  // back-fill alias entries that the operator typed without an explicit
  // /N suffix ("1.2.3.5" → "1.2.3.5/<primary subnet's prefix>").
  const subnetMaskToCidr = (mask: string): number => {
    const map: Record<string, number> = {
      '255.255.255.252': 30, '255.255.255.248': 29, '255.255.255.240': 28,
      '255.255.255.224': 27, '255.255.255.192': 26, '255.255.255.128': 25,
      '255.255.255.0': 24, '255.255.254.0': 23, '255.255.252.0': 22,
      '255.255.248.0': 21, '255.255.240.0': 20, '255.255.224.0': 19,
      '255.255.192.0': 18, '255.255.128.0': 17, '255.255.0.0': 16,
      '255.254.0.0': 15, '255.252.0.0': 14, '255.248.0.0': 13,
      '255.240.0.0': 12, '255.0.0.0': 8,
    };
    return map[mask] ?? 24;
  };

  // VSNP-713: do two IPv4 CIDR strings share any address space? If yes,
  // the alias reuses the primary's default route (no gateway needed).
  // If no, the alias is on a different subnet and the operator must
  // supply a gateway so the agent can install a source-policy route.
  const aliasOverlapsPrimary = (aliasCidr: string, primaryCidr: string): boolean => {
    const parse = (cidr: string): [number, number] | null => {
      const [ipStr, prefixStr] = cidr.split('/');
      if (!ipStr || !prefixStr) return null;
      const prefix = parseInt(prefixStr, 10);
      if (Number.isNaN(prefix) || prefix < 0 || prefix > 32) return null;
      const parts = ipStr.split('.').map((p) => parseInt(p, 10));
      if (parts.length !== 4 || parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return null;
      const ip = ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
      return [ip, prefix];
    };
    const a = parse(aliasCidr);
    const b = parse(primaryCidr);
    if (!a || !b) return true; // can't decide → assume same-subnet to avoid spurious "gateway required" errors while user is mid-type
    const maskBits = Math.min(a[1], b[1]);
    const mask = maskBits === 0 ? 0 : (~0 << (32 - maskBits)) >>> 0;
    return (a[0] & mask) === (b[0] & mask);
  };

  // Build the primary CIDR string for overlap testing. Falls back to a
  // /24 if the primary doesn't have a /N — same defaulting logic that
  // subnetMaskToCidr uses elsewhere.
  const primaryCidrForOverlap = (): string => {
    const ipBare = (formData.ipv4 || '').split('/')[0];
    if (!ipBare) return '';
    const prefix = formData.ipv4.includes('/')
      ? parseInt(formData.ipv4.split('/')[1] || '24', 10)
      : subnetMaskToCidr(formData.subnet);
    return `${ipBare}/${prefix}`;
  };

  // Auto-generate interface name when VLAN ID and physical interface change
  const getGeneratedName = () => {
    if (formData.vlanId && formData.physicalInterface) {
      const selectedPort = sortedPhysicalInterfaces.find((pi) => pi.id === formData.physicalInterface);
      const portName = selectedPort?.name || formData.physicalInterface;
      return `${portName}.${formData.vlanId}`;
    }
    return '';
  };

  const handleSubmit = async () => {
    if (isBridgedWAN) {
      toast.error('Invalid Configuration', 'Bridged WAN interfaces are not supported. Change Type to Routed.');
      return;
    }
    // VSNP-659: at least one physical interface must be selected. For
    // Bridged LAN we also reject 0 members; the Routed case still uses
    // the single picker so physicalInterface is the truth there.
    const isBridgedLAN = formData.role === 'LAN' && formData.type === 'Bridged';
    const memberIds = isBridgedLAN
      ? formData.physicalInterfaceIds
      : (formData.physicalInterface ? [formData.physicalInterface] : []);
    if (!formData.name || memberIds.length === 0) {
      toast.error('Validation Error', isBridgedLAN
        ? 'Select at least one physical interface to bridge'
        : 'Please fill in all required fields');
      return;
    }

    // VSNP-690: warn when this WAN-role interface shares a VRF with another
    // sibling WAN. Same-VRF multi-WAN re-creates the subnet-conflict that
    // VRFs are supposed to avoid (VPP rejects two interfaces with the same
    // subnet in one FIB). Soft block with confirmation — operator can opt
    // through if they understand the consequence (e.g. distinct subnets).
    if (formData.role === 'WAN') {
      const conflict = siblingInterfaces.find((si) =>
        si.id !== iface?.id &&
        si.role === 'WAN' &&
        (si.vrfId || '') === (formData.vrf || '')
      );
      if (conflict) {
        const conflictName = conflict.name || conflict.id;
        const proceed = window.confirm(
          `Another WAN interface (${conflictName}) is already using this VRF. ` +
          `Two WAN interfaces in the same VRF cannot have IPs in the same subnet ` +
          `(VPP rejects the second IP install).\n\n` +
          `Click OK to save anyway, or Cancel to pick a different VRF (use "+ New VRF" if needed).`
        );
        if (!proceed) return;
      }
    }

    // VSNP-691 + VSNP-713: clean alias list — drop empty entries, append
    // /N from the primary subnet selection when the operator omitted the
    // prefix on the CIDR, and produce `{cidr, gateway?}` objects. Gateway
    // is required when the alias CIDR doesn't overlap the primary's CIDR;
    // the server enforces this too, but we surface it client-side so the
    // operator sees the rule before a 400.
    const primaryPrefix = formData.subnet ? subnetMaskToCidr(formData.subnet) : 24;
    const primaryCidrStr = primaryCidrForOverlap();
    const cleanAliases: Array<{ cidr: string; gateway?: string }> = [];
    for (const entry of formData.ipv4Aliases || []) {
      const cidrRaw = (entry?.cidr || '').trim();
      if (!cidrRaw) continue;
      const cidr = cidrRaw.includes('/') ? cidrRaw : `${cidrRaw}/${primaryPrefix}`;
      const gwRaw = (entry?.gateway || '').trim();
      const needsGateway = !!primaryCidrStr && !aliasOverlapsPrimary(cidr, primaryCidrStr);
      if (needsGateway && !gwRaw) {
        toast.error(`Alias ${cidr} is on a different subnet from the primary — please supply its gateway.`);
        return;
      }
      cleanAliases.push(gwRaw ? { cidr, gateway: gwRaw } : { cidr });
    }

    const apiData = {
      port_id: memberIds[0],
      port_ids: memberIds, // VSNP-659: full list (Bridged LAN uses 2+, others use 1)
      logical_name: formData.name,
      vlan_id: formData.vlanId ? parseInt(formData.vlanId.toString()) : undefined,
      role: formData.role,
      type: formData.type,
      ipv4: addressingDisabled ? undefined : formData.ipv4 || undefined,
      ipv4_aliases: addressingDisabled ? [] : cleanAliases,
      subnet_mask: addressingDisabled ? undefined : formData.subnet || undefined,
      ipv6: undefined,
      gateway: addressingDisabled ? undefined : formData.gateway || undefined,
      dhcp_client: !!formData.dhcpClient && showDhcpClient,
      vrf_id: formData.vrf || undefined,
      mtu: formData.mtu,
      mss: formData.mss || undefined,
      enabled: true,
      description: formData.description || undefined,
    };

    setSaving(true);
    try {
      if (mode === 'add' && onCommit) {
        // Add mode: atomically commit the interface + every drafted subscription
        // in one request. A single Save persists everything. Strip the
        // local-only _draftId from each draft first.
        const subscriptions = draftSubscriptions.map((d) => {
          const sub = { ...d };
          delete sub._draftId;
          return sub;
        });
        await onCommit({ interface: apiData, subscriptions });
        toast.success(
          'Interface created',
          subscriptions.length
            ? `Saved with ${subscriptions.length} subscription${subscriptions.length === 1 ? '' : 's'}.`
            : 'Interface saved.',
        );
      } else {
        // Edit mode: the interface already exists (and its subscriptions are
        // created/updated live in handleSaveSubscription) — just persist the
        // interface fields.
        await onSave(apiData);
      }
      onClose();
    } catch (err: any) {
      // Surface the API's actual reason (e.g. "Gateway X must be in the same
      // subnet as interface IP Y"), not axios's generic "status code 400".
      toast.error('Save Failed', apiErrorMessage(err, 'Could not save interface'));
    } finally {
      setSaving(false);
    }
  };

  const closeSubModals = () => {
    setShowDHCPModal(false);
    setShowSNATModal(false);
    setShowPFModal(false);
    setEditingSubscription(null);
  };

  const handleSaveSubscription = async (data: any) => {
    if (!edgeId) return;

    // Add mode: the interface isn't persisted yet, so hold the subscription as a
    // client-side draft and commit it atomically with the interface on Save.
    if (!hasPersistedInterface) {
      setDraftSubscriptions((prev) => {
        if (editingSubscription?._draftId != null) {
          return prev.map((d) =>
            d._draftId === editingSubscription._draftId ? { ...data, _draftId: d._draftId } : d,
          );
        }
        return [...prev, { ...data, _draftId: ++draftIdCounter.current }];
      });
      toast.success(
        editingSubscription?._draftId != null ? 'Draft updated' : 'Draft added',
        'It will be saved when you create the interface.',
      );
      closeSubModals();
      return;
    }

    // Edit mode: the interface exists — create/update the subscription directly.
    try {
      if (editingSubscription) {
        await edgeService.updateSubscription(edgeId, editingSubscription.id, data);
        toast.success('Saved', 'Subscription updated.');
      } else {
        await edgeService.createSubscription(edgeId, data);
        toast.success('Added', 'Subscription added.');
      }
      await loadSubscriptions();
    } catch (err: any) {
      toast.error('Save Failed', apiErrorMessage(err, 'Could not save subscription'));
    } finally {
      closeSubModals();
    }
  };

  const handleDeleteSubscription = async (id: string, name: string) => {
    // Add mode: removing a not-yet-saved draft — drop it from state, no server
    // call and no destructive-delete confirm (nothing is persisted).
    if (!hasPersistedInterface) {
      setDraftSubscriptions((prev) => prev.filter((d) => String(d._draftId) !== String(id)));
      return;
    }
    // Saved subscription — confirm via the platform dialog (not window.confirm).
    setSubDeleteConfirm({ id, name });
  };

  const confirmDeleteSubscription = async () => {
    if (!edgeId || !subDeleteConfirm) return;
    setSubDeleting(true);
    try {
      await edgeService.deleteSubscription(edgeId, subDeleteConfirm.id);
      toast.success('Deleted', 'Subscription removed.');
      await loadSubscriptions();
      setSubDeleteConfirm(null);
    } catch (err: any) {
      toast.error('Delete Failed', apiErrorMessage(err, 'Could not delete subscription'));
    } finally {
      setSubDeleting(false);
    }
  };

  const openSubEdit = (sub: any) => {
    setEditingSubscription(sub);
    if (sub.subscription_type === 'dhcp_pool') setShowDHCPModal(true);
    else if (sub.subscription_type === 'source_nat') setShowSNATModal(true);
    else if (sub.subscription_type === 'port_forwarding') setShowPFModal(true);
  };

  // Show already-saved subscriptions (edit mode) or the client-side drafts
  // being composed for a new interface (add mode). Drafts are tagged _isDraft
  // (row shows a "Draft" badge) and expose `id` (= their _draftId) so the
  // list's edit/remove wiring works unchanged.
  const draftItems = draftSubscriptions.map((d) => ({ ...d, id: d._draftId, _isDraft: true }));
  const subsForDisplay = hasPersistedInterface ? subscriptions : draftItems;
  const dhcpPools = subsForDisplay.filter((s) => s.subscription_type === 'dhcp_pool');
  const sourceNats = subsForDisplay.filter((s) => s.subscription_type === 'source_nat');
  const portForwards = subsForDisplay.filter((s) => s.subscription_type === 'port_forwarding');
  // In add mode the interface isn't persisted, but subscriptions can still be
  // drafted — the Add buttons and lists are enabled off this flag.
  const canManageSubs = hasPersistedInterface || mode === 'add';

  // DHCP server pools are only valid on a Bridged LAN (the edge binds dnsmasq
  // to the bridge-domain's loop0 BVI, which exists only in Bridged mode). When
  // unavailable, the Add DHCP Pool button is shown disabled with a reason
  // rather than hidden, so the capability stays discoverable. In edit mode the
  // Type radios are locked (below), so this reflects the saved type.
  const dhcpPoolsAvailable = formData.role === 'LAN' && formData.type === 'Bridged';
  const dhcpUnavailableReason = 'DHCP server pools require a Bridged LAN interface — set Type = Bridged above.';

  // The backend forbids flipping an existing interface across the
  // Bridged ↔ non-Bridged boundary (it's a full VPP topology change; requires
  // delete + recreate). In edit mode, lock the Type radios accordingly so the
  // operator can't attempt a conversion that will always be rejected.
  const savedBridgedLan = iface?.role === 'LAN' && iface?.type === 'Bridged';
  const isEditingExisting = mode === 'edit' && !!iface;
  const disableBridgedRadio = isEditingExisting && !savedBridgedLan;
  const disableRoutedRadio = isEditingExisting && savedBridgedLan;
  const typeLockReason =
    "An interface's type can't be switched between Bridged and Routed after creation (it changes the dataplane topology). Delete and recreate the interface to change it.";

  // Inline validation for the "+ New VRF" form: the VPP table id and the name
  // must both be unique among existing VRFs (the backend enforces this too), and
  // the id must be in range. Drives the field highlights + disabled Create VRF.
  const newVrfTableIdInRange = newVrfTableId >= 1 && newVrfTableId <= 65535;
  const newVrfTableIdClash = vrfOptions.find((v) => v.vrfId === newVrfTableId);
  const newVrfNameClash = vrfOptions.find(
    (v) => !!newVrfName.trim() && (v.name || '').trim().toLowerCase() === newVrfName.trim().toLowerCase(),
  );
  const newVrfError = !newVrfName.trim()
    ? null
    : newVrfNameClash
      ? `A VRF named "${newVrfName.trim()}" already exists — pick a different name.`
      : !newVrfTableIdInRange
        ? 'VPP Table ID must be between 1 and 65535.'
        : newVrfTableIdClash
          ? `VPP Table ID ${newVrfTableId} is already used by "${newVrfTableIdClash.name}" — pick a different id.`
          : null;
  const newVrfValid =
    !!newVrfName.trim() && newVrfTableIdInRange && !newVrfTableIdClash && !newVrfNameClash;

  // Identity handed to the subscription sub-modals while the interface is still
  // a draft (add mode), so SNAT/Port-Forward can resolve the WAN name and
  // aliases without the interface existing on the server yet.
  const draftInterfaceContext = {
    name: formData.name,
    role: formData.role,
    ipv4: formData.ipv4,
    ipv4Aliases: formData.ipv4Aliases,
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary" disabled={saving}>
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
        disabled={saving || isBridgedWAN}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        <span>{mode === 'add' ? 'Create Interface' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          mode === 'edit'
            ? `Edit Logical Interface: ${iface?.name || formData.name}`
            : 'Add Logical Interface'
        }
        size="2xl"
        footer={footer}
      >
        <div className="space-y-6">
          {/* ============================================================
              Section: Identity
              ============================================================ */}
          <SectionHeader icon={Network} title="Identity" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Physical Interface{formData.role === 'LAN' && formData.type === 'Bridged' ? 's' : ''}{' '}
                <span className="text-red-500">*</span>
              </label>
              {formData.role === 'LAN' && formData.type === 'Bridged' ? (
                // VSNP-659: multi-select chip picker for Bridged LAN. Every
                // checked entry is added as an L2 member of the same VPP
                // bridge-domain alongside the BVI (loop0) so all the wired
                // ports share one L2 broadcast domain.
                <div className="border border-gray-300 rounded-md p-2 max-h-40 overflow-y-auto bg-white">
                  {sortedPhysicalInterfaces.length === 0 ? (
                    <div className="text-sm text-gray-500 px-2 py-1">No physical interfaces available</div>
                  ) : (
                    sortedPhysicalInterfaces.map((pi) => {
                      const checked = formData.physicalInterfaceIds.includes(pi.id);
                      return (
                        <label
                          key={pi.id}
                          className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.physicalInterfaceIds, pi.id]
                                : formData.physicalInterfaceIds.filter((id) => id !== pi.id);
                              setFormData((prev) => ({
                                ...prev,
                                physicalInterfaceIds: next,
                                physicalInterface: next[0] || '',
                              }));
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="font-medium text-gray-900">{pi.name}</span>
                          {pi.configuredState === false && (
                            <span className="text-xs text-gray-500">(disabled)</span>
                          )}
                        </label>
                      );
                    })
                  )}
                  <div className="text-xs text-gray-500 mt-2 px-2">
                    Select 1 or more ports. All selected ports will share the same L2 broadcast domain.
                  </div>
                </div>
              ) : (
                <select
                  value={formData.physicalInterface}
                  onChange={(e) => {
                    handleChange('physicalInterface', e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      physicalInterface: e.target.value,
                      physicalInterfaceIds: e.target.value ? [e.target.value] : [],
                    }));
                    const selectedPort = sortedPhysicalInterfaces.find((pi) => pi.id === e.target.value);
                    const portName = selectedPort?.name || e.target.value;
                    if (formData.vlanId && e.target.value) {
                      handleChange('name', `${portName}.${formData.vlanId}`);
                    }
                  }}
                  className="input-base w-full"
                  required
                >
                  <option value="">Select physical interface</option>
                  {sortedPhysicalInterfaces.map((pi) => (
                    <option key={pi.id} value={pi.id}>
                      {pi.name}
                      {pi.configuredState === false ? ' (disabled)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">VLAN ID</label>
              <input
                type="number"
                value={formData.vlanId}
                onChange={(e) => {
                  const vlanId = e.target.value;
                  handleChange('vlanId', vlanId);
                  const selectedPort = sortedPhysicalInterfaces.find((pi) => pi.id === formData.physicalInterface);
                  const portName = selectedPort?.name;
                  if (vlanId && portName) {
                    handleChange('name', `${portName}.${vlanId}`);
                  } else if (!vlanId && portName) {
                    handleChange('name', portName);
                  }
                }}
                placeholder="e.g., 100"
                min={1}
                max={4094}
                className="input-base w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional: VLAN tag (1–4094). Creates a sub-interface automatically.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interface Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={getGeneratedName() || 'e.g., eth0, eth1.100, tun0'}
                className="input-base w-full"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.vlanId
                  ? 'Auto-generated from physical interface and VLAN ID'
                  : 'Use VLAN notation for sub-interfaces (e.g., eth1.100)'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="e.g., Primary WAN, Guest VLAN"
                className="input-base w-full"
              />
            </div>
          </div>

          {/* ============================================================
              Section: Role & Type
              ============================================================ */}
          <SectionHeader icon={Settings} title="Role & Type" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="WAN"
                    checked={formData.role === 'WAN'}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">WAN</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="LAN"
                    checked={formData.role === 'LAN'}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">LAN</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-4">
                <label className={`inline-flex items-center ${disableRoutedRadio ? 'opacity-50 cursor-not-allowed' : ''}`} title={disableRoutedRadio ? typeLockReason : undefined}>
                  <input
                    type="radio"
                    value="Routed"
                    checked={formData.type === 'Routed'}
                    disabled={disableRoutedRadio}
                    onChange={(e) => handleChange('type', e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Routed</span>
                </label>
                <label className={`inline-flex items-center ${disableBridgedRadio ? 'opacity-50 cursor-not-allowed' : ''}`} title={disableBridgedRadio ? typeLockReason : undefined}>
                  <input
                    type="radio"
                    value="Bridged"
                    checked={formData.type === 'Bridged'}
                    disabled={disableBridgedRadio}
                    onChange={(e) => handleChange('type', e.target.value)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Bridged</span>
                </label>
              </div>
              {(disableBridgedRadio || disableRoutedRadio) && (
                <p className="mt-1 text-xs text-gray-500">
                  Type is fixed after creation — {savedBridgedLan ? 'this is a Bridged LAN' : 'switching to Bridged'} would
                  change the dataplane topology. To change it, delete and recreate the interface.
                </p>
              )}
            </div>
          </div>
          {isBridgedWAN && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">
                <strong>Bridged WAN is not supported.</strong> WAN interfaces must be Routed
                so traffic can be NATed and routed upstream. Change Type back to Routed.
              </p>
            </div>
          )}
          {roleChanged && incompatibleSubs.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Role change will orphan {incompatibleSubs.length} subscription
                {incompatibleSubs.length === 1 ? '' : 's'}.</strong>
                <p className="mt-1">
                  The following {formData.role === 'WAN' ? 'DHCP pools' : 'NAT / Port Forwarding rules'}
                  {' '}belong under a {originalRole} interface and won't work under {formData.role}.
                  Delete them from the sections below before saving, or revert the role.
                </p>
                <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
                  {incompatibleSubs.slice(0, 5).map((s) => (
                    <li key={s.id}>
                      <span className="font-mono">{s.subscription_name || s.id}</span>
                      {' '}<span className="text-amber-600">({s.subscription_type})</span>
                    </li>
                  ))}
                  {incompatibleSubs.length > 5 && (
                    <li className="italic">
                      …and {incompatibleSubs.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* ============================================================
              Section: Addressing
              ============================================================ */}
          <SectionHeader icon={Network} title="Addressing" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {showDhcpClient && (
              <div className="md:col-span-2">
                <label className="inline-flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.dhcpClient}
                    onChange={(e) => handleChange('dhcpClient', e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Use DHCP Client (auto-configure)</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  {formData.role === 'WAN'
                    ? 'Typical for WAN interfaces on ISP-provided connections.'
                    : 'Uncommon on LAN. Leave disabled to use a static IP.'}
                </p>
              </div>
            )}
            {!showDhcpClient && (
              <div className="md:col-span-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600">
                  A bridged LAN interface defines the bridge's static IP — clients on the bridge
                  receive IPs via any DHCP pools configured below, not from this interface itself.
                </p>
              </div>
            )}

            {/* When DHCP Client is on, the static-IP fields are hidden
                entirely — the interface gets its address from the upstream
                DHCP server and manual values would be ignored. */}
            {!addressingDisabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IPv4 Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.ipv4}
                    onChange={(e) => handleChange('ipv4', e.target.value)}
                    placeholder="e.g., 192.168.1.1"
                    className="input-base w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subnet Mask <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.subnet}
                    onChange={(e) => handleChange('subnet', e.target.value)}
                    className="input-base w-full"
                    required
                  >
                    <option value="255.255.255.252">/30 (255.255.255.252)</option>
                    <option value="255.255.255.248">/29 (255.255.255.248)</option>
                    <option value="255.255.255.240">/28 (255.255.255.240)</option>
                    <option value="255.255.255.224">/27 (255.255.255.224)</option>
                    <option value="255.255.255.192">/26 (255.255.255.192)</option>
                    <option value="255.255.255.128">/25 (255.255.255.128)</option>
                    <option value="255.255.255.0">/24 (255.255.255.0)</option>
                    <option value="255.255.254.0">/23 (255.255.254.0)</option>
                    <option value="255.255.252.0">/22 (255.255.252.0)</option>
                    <option value="255.255.248.0">/21 (255.255.248.0)</option>
                    <option value="255.255.240.0">/20 (255.255.240.0)</option>
                    <option value="255.255.224.0">/19 (255.255.224.0)</option>
                    <option value="255.255.192.0">/18 (255.255.192.0)</option>
                    <option value="255.255.128.0">/17 (255.255.128.0)</option>
                    <option value="255.255.0.0">/16 (255.255.0.0)</option>
                    <option value="255.254.0.0">/15 (255.254.0.0)</option>
                    <option value="255.252.0.0">/14 (255.252.0.0)</option>
                    <option value="255.248.0.0">/13 (255.248.0.0)</option>
                    <option value="255.240.0.0">/12 (255.240.0.0)</option>
                    <option value="255.0.0.0">/8 (255.0.0.0)</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Gateway
                  </label>
                  <input
                    type="text"
                    value={formData.gateway}
                    onChange={(e) => handleChange('gateway', e.target.value)}
                    placeholder="e.g., 192.168.1.254"
                    className="input-base w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.role === 'WAN'
                      ? 'Required for WAN interfaces with a static IP'
                      : 'Optional for LAN interfaces'}
                  </p>
                </div>

                {/* VSNP-691: alias addresses on the same interface. Only
                    surfaced for WAN-role static interfaces — DHCP+aliases
                    is intentionally not supported in v1, and LAN aliases
                    aren't a Phase-1 ask. */}
                {formData.role === 'WAN' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional IP addresses
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        (alias addresses on this interface)
                      </span>
                    </label>
                    {formData.ipv4Aliases.length === 0 && (
                      <p className="text-xs text-gray-500 mb-2">
                        Use this when an ISP allocates extra public IPs on
                        the same uplink — including IPs from a different
                        subnet, which need their own gateway. Each alias
                        can host its own port-forwarding rules.
                      </p>
                    )}
                    <div className="space-y-2">
                      {formData.ipv4Aliases.map((aliasRaw: any, idx) => {
                        // Defensive normalisation in case stale data (a legacy
                        // string-form alias, or a JSON-string of an object)
                        // sneaks past the hydration map. Without this guard,
                        // React would render the object via toString() and the
                        // operator would see literally "[object Object]" in
                        // the input. Coerce here so the field always shows
                        // something useful.
                        let alias: { cidr: string; gateway?: string };
                        if (typeof aliasRaw === 'string') {
                          alias = { cidr: aliasRaw };
                        } else if (aliasRaw && typeof aliasRaw === 'object') {
                          alias = {
                            cidr: typeof aliasRaw.cidr === 'string' ? aliasRaw.cidr : '',
                            gateway: typeof aliasRaw.gateway === 'string' ? aliasRaw.gateway : undefined,
                          };
                        } else {
                          alias = { cidr: '' };
                        }
                        const primaryCidrStr = primaryCidrForOverlap();
                        const aliasCidr = (alias.cidr || '').trim();
                        // Only force the gateway field once the alias has
                        // a clearly different subnet AND a fully-typed
                        // CIDR. While the user is still typing the alias
                        // field, keep the gateway hidden.
                        const cidrHasPrefix = aliasCidr.includes('/');
                        const isDifferentSubnet = cidrHasPrefix && !!primaryCidrStr && !aliasOverlapsPrimary(aliasCidr, primaryCidrStr);
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={alias.cidr || ''}
                                onChange={(e) => {
                                  const next = [...formData.ipv4Aliases];
                                  next[idx] = { ...next[idx], cidr: e.target.value };
                                  handleChange('ipv4Aliases', next);
                                }}
                                placeholder={`e.g., ${formData.ipv4 ? formData.ipv4.split('.').slice(0, 3).join('.') + '.' : '1.2.3.'}5/${subnetMaskToCidr(formData.subnet)}`}
                                className="input-base flex-1 font-mono text-sm"
                              />
                              {isDifferentSubnet && (
                                <input
                                  type="text"
                                  value={alias.gateway || ''}
                                  onChange={(e) => {
                                    const next = [...formData.ipv4Aliases];
                                    next[idx] = { ...next[idx], gateway: e.target.value };
                                    handleChange('ipv4Aliases', next);
                                  }}
                                  placeholder="Alias gateway"
                                  className="input-base w-48 font-mono text-sm"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const next = formData.ipv4Aliases.filter((_, i) => i !== idx);
                                  handleChange('ipv4Aliases', next);
                                }}
                                className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                                title="Remove alias"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {isDifferentSubnet && (
                              <p className="text-xs text-amber-700">
                                This alias is on a different subnet from the primary — supply its gateway so the edge can source-route egress.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        handleChange(
                          'ipv4Aliases' as any,
                          [...formData.ipv4Aliases, { cidr: '' }] as any,
                        );
                      }}
                      className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="w-4 h-4" />
                      Add IP address
                    </button>
                  </div>
                )}
              </>
            )}
            {addressingDisabled && (
              <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
                <Network className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">
                  IPv4 address, subnet, and gateway will be auto-configured
                  from the upstream DHCP server when this interface comes up.
                </p>
              </div>
            )}
          </div>

          {/* ============================================================
              Section: DHCP Server Pools (LAN only)
              ============================================================ */}
          {formData.role === 'LAN' && (
            <>
              <SectionHeader
                icon={Server}
                title="DHCP Server Pools"
                action={canManageSubs && canCreate && (
                  <button
                    type="button"
                    disabled={!dhcpPoolsAvailable}
                    onClick={dhcpPoolsAvailable ? () => { setEditingSubscription(null); setShowDHCPModal(true); } : undefined}
                    title={dhcpPoolsAvailable ? undefined : dhcpUnavailableReason}
                    className={`btn-secondary text-sm inline-flex items-center space-x-1.5 ${
                      dhcpPoolsAvailable ? '' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add DHCP Pool</span>
                  </button>
                )}
              />
              {/* DHCP server pools are only valid on a Bridged LAN — the edge
                  binds dnsmasq to the bridge-domain's loop0 BVI, which only
                  exists in Bridged mode. Show the list only then; on a Routed
                  LAN explain why (the Add button above is disabled). */}
              {dhcpPoolsAvailable ? (
                <SubscriptionList
                  items={dhcpPools}
                  loading={subsLoading}
                  hasPersistedInterface={canManageSubs}
                  emptyPlaceholder="No DHCP pools on this interface — clients will need static IPs or an external DHCP server."
                  saveFirstPlaceholder="Save the interface first, then add DHCP pools here."
                  renderRow={(s) => (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{s.subscription_name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {s.configuration?.start_ip} — {s.configuration?.end_ip}
                          {s.configuration?.subnet_mask ? ` (${s.configuration.subnet_mask})` : ''}
                        </p>
                      </div>
                    </>
                  )}
                  onEdit={openSubEdit}
                  onDelete={handleDeleteSubscription}
                />
              ) : (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p>{dhcpUnavailableReason}</p>
                </div>
              )}
            </>
          )}

          {/* ============================================================
              Section: Source NAT (WAN only, Routed only)
              ============================================================ */}
          {formData.role === 'WAN' && !isBridgedWAN && (
            <>
              <SectionHeader
                icon={Globe}
                title="Source NAT"
                action={canManageSubs && canCreate && (
                  <button
                    type="button"
                    onClick={() => { setEditingSubscription(null); setShowSNATModal(true); }}
                    className="btn-secondary text-sm inline-flex items-center space-x-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Source NAT</span>
                  </button>
                )}
              />
              <SubscriptionList
                items={sourceNats}
                loading={subsLoading}
                hasPersistedInterface={canManageSubs}
                emptyPlaceholder="No Source NAT rules on this interface yet."
                saveFirstPlaceholder="Save the interface first, then add Source NAT rules here."
                renderRow={(s) => (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.subscription_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.configuration?.source_network || s.configuration?.in_ip || '—'}
                      {' → '}
                      {s.configuration?.translated_address || s.configuration?.out_ip || 'interface IP'}
                    </p>
                  </div>
                )}
                onEdit={openSubEdit}
                onDelete={handleDeleteSubscription}
              />

              <SectionHeader
                icon={ArrowRightLeft}
                title="Port Forwarding"
                action={canManageSubs && canCreate && (
                  <button
                    type="button"
                    onClick={() => { setEditingSubscription(null); setShowPFModal(true); }}
                    className="btn-secondary text-sm inline-flex items-center space-x-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Port Forward</span>
                  </button>
                )}
              />
              <SubscriptionList
                items={portForwards}
                loading={subsLoading}
                hasPersistedInterface={canManageSubs}
                emptyPlaceholder="No port forwards on this interface yet."
                saveFirstPlaceholder="Save the interface first, then add port forwards here."
                renderRow={(s) => {
                  // VSNP-713: surface the external IP when:
                  //   - the operator explicitly pinned this forward to an
                  //     IP (alias picker, non-empty external_ip), OR
                  //   - this interface has aliases configured (so the
                  //     operator needs to see whether "no pin" means the
                  //     forward defaults to the primary).
                  // Single-IP WANs keep the terse "proto port → ip:port"
                  // summary.
                  const hasAliases = (formData.ipv4Aliases || []).length > 0;
                  const explicitExt = (s.configuration?.external_ip || '').trim();
                  const primaryBare = (formData.ipv4 || '').split('/')[0];
                  const extLabel = hasAliases
                    ? (explicitExt || `${primaryBare || 'primary'} (primary)`)
                    : explicitExt;
                  return (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{s.subscription_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {(s.configuration?.protocol || 'TCP').toUpperCase()}
                        {extLabel ? ` ${extLabel}:` : ' '}
                        {s.configuration?.external_port}
                        {' → '}
                        {s.configuration?.internal_ip}:{s.configuration?.internal_port}
                      </p>
                    </div>
                  );
                }}
                onEdit={openSubEdit}
                onDelete={handleDeleteSubscription}
              />
            </>
          )}

          {/* ============================================================
              Section: Advanced (collapsed by default)
              ============================================================ */}
          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className="w-full flex items-center justify-between py-2 border-b border-gray-200 hover:bg-gray-50 -mx-2 px-2 rounded"
            >
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">Advanced</span>
              </div>
              {advancedOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>
            {advancedOpen && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MTU</label>
                  <input
                    type="number"
                    value={formData.mtu}
                    onChange={(e) => handleChange('mtu', parseInt(e.target.value))}
                    min={576}
                    max={9000}
                    className="input-base w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">Default: 1500 bytes</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MSS</label>
                  <input
                    type="number"
                    value={formData.mss}
                    onChange={(e) => handleChange('mss', parseInt(e.target.value))}
                    min={536}
                    max={8960}
                    className="input-base w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">Default: 1460 bytes</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VRF (Virtual Routing & Forwarding)
                    {vrfLoading && <Loader2 className="w-3 h-3 inline ml-2 animate-spin" />}
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.vrf}
                      onChange={(e) => handleChange('vrf', e.target.value)}
                      className="input-base flex-1"
                      disabled={vrfLoading}
                    >
                      {vrfOptions.map((vrf) => (
                        <option key={vrf.id} value={vrf.id}>
                          {vrf.name}{vrf.vrfId !== undefined ? ` (ID: ${vrf.vrfId})` : ''}{vrf.isDefault ? ' - Default' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setNewVrfTableId(suggestNextVrfTableId(vrfOptions));
                        setNewVrfName('');
                        setNewVrfDescription('');
                        setNewVrfFormOpen(true);
                      }}
                      className="btn-secondary text-sm whitespace-nowrap"
                    >
                      + New VRF
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Assign this interface to a VRF for network isolation. For
                    multi-WAN setups in the same subnet, give each WAN its own
                    VRF.
                  </p>

                  {newVrfFormOpen && (
                    <div className="mt-3 p-3 border rounded bg-gray-50 space-y-2">
                      <div className="text-sm font-medium text-gray-700">Create new VRF</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600">Name</label>
                          <input
                            type="text"
                            value={newVrfName}
                            onChange={(e) => setNewVrfName(e.target.value)}
                            placeholder="e.g. wan-secondary"
                            className={`input-base w-full text-sm ${newVrfNameClash ? 'border-red-500' : ''}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">VPP Table ID (1-65535)</label>
                          <input
                            type="number"
                            value={newVrfTableId}
                            onChange={(e) => setNewVrfTableId(parseInt(e.target.value) || 0)}
                            min={1}
                            max={65535}
                            className={`input-base w-full text-sm ${newVrfTableIdClash || !newVrfTableIdInRange ? 'border-red-500' : ''}`}
                          />
                        </div>
                      </div>
                      {newVrfError && (
                        <p className="text-xs text-red-600">{newVrfError}</p>
                      )}
                      <div>
                        <label className="block text-xs text-gray-600">Description (optional)</label>
                        <input
                          type="text"
                          value={newVrfDescription}
                          onChange={(e) => setNewVrfDescription(e.target.value)}
                          className="input-base w-full text-sm"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setNewVrfFormOpen(false)}
                          className="btn-secondary text-sm"
                          disabled={newVrfSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!newVrfValid) return; // inline error + disabled button explain why
                            setNewVrfSaving(true);
                            try {
                              const resp = await api.post('/api/v2/vrfs', {
                                name: newVrfName.trim(),
                                description: newVrfDescription.trim() || undefined,
                                vrfId: newVrfTableId,
                                // Scope the new VRF to the edge's tenant so
                                // the assignment doesn't get rejected by
                                // validateVRFAccess. Without this, a
                                // SuperAdmin's tenantId is used and the
                                // edge's tenant won't match.
                                edgeId: edgeId,
                              });
                              const created = resp.data;
                              const newOpt: VRFOption = {
                                id: created.id,
                                name: created.name,
                                description: created.description,
                                vrfId: created.vrfId ?? newVrfTableId,
                                isDefault: !!created.isDefault,
                              };
                              setVrfOptions((prev) => [...prev, newOpt]);
                              handleChange('vrf', created.id);
                              setNewVrfFormOpen(false);
                              toast.success('VRF created', `${created.name} (ID: ${newOpt.vrfId}) ready to use`);
                            } catch (err: any) {
                              toast.error('Failed to create VRF', apiErrorMessage(err, 'Could not create VRF'));
                            } finally {
                              setNewVrfSaving(false);
                            }
                          }}
                          className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={newVrfSaving || !newVrfValid}
                        >
                          {newVrfSaving ? 'Creating...' : 'Create VRF'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* ============================================================
          Embedded subscription sub-modals — interface pre-locked
          ============================================================ */}
      {edgeId && (effectiveInterfaceId || mode === 'add') && (
        <>
          <DHCPPoolModal
            isOpen={showDHCPModal}
            onClose={() => { setShowDHCPModal(false); setEditingSubscription(null); }}
            onSave={handleSaveSubscription}
            subscription={editingSubscription}
            mode={editingSubscription ? 'edit' : 'add'}
            edgeId={edgeId}
            lockedInterfaceId={effectiveInterfaceId || undefined}
            draftInterface={effectiveInterfaceId ? undefined : draftInterfaceContext}
            existingDhcpPools={dhcpPools}
          />
          <SourceNATModal
            isOpen={showSNATModal}
            onClose={() => { setShowSNATModal(false); setEditingSubscription(null); }}
            onSave={handleSaveSubscription}
            subscription={editingSubscription}
            mode={editingSubscription ? 'edit' : 'add'}
            edgeId={edgeId}
            lockedInterfaceId={effectiveInterfaceId || undefined}
            draftInterface={effectiveInterfaceId ? undefined : draftInterfaceContext}
          />
          <PortForwardingModal
            isOpen={showPFModal}
            onClose={() => { setShowPFModal(false); setEditingSubscription(null); }}
            onSave={handleSaveSubscription}
            subscription={editingSubscription}
            mode={editingSubscription ? 'edit' : 'add'}
            edgeId={edgeId}
            lockedInterfaceId={effectiveInterfaceId || undefined}
            draftInterface={effectiveInterfaceId ? undefined : draftInterfaceContext}
          />
        </>
      )}

      {/* Platform confirm dialog for deleting a saved subscription — replaces
          the native window.confirm so it matches the rest of the UI. */}
      <ConfirmDialog
        isOpen={!!subDeleteConfirm}
        onClose={() => { if (!subDeleting) setSubDeleteConfirm(null); }}
        onConfirm={confirmDeleteSubscription}
        title="Delete Subscription"
        message={
          <>
            Delete <strong>{subDeleteConfirm?.name || 'this subscription'}</strong>? This can't be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={subDeleting}
        size="sm"
      />
    </>
  );
};

// ================================================================
// Small presentation helpers
// ================================================================

const SectionHeader = ({
  icon: Icon,
  title,
  action,
}: {
  icon: typeof Network;
  title: string;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between border-b border-gray-200 pb-2">
    <div className="flex items-center space-x-2">
      <Icon className="w-4 h-4 text-gray-500" />
      <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
    </div>
    {action}
  </div>
);

const SubscriptionList = ({
  items,
  loading,
  hasPersistedInterface,
  emptyPlaceholder,
  saveFirstPlaceholder,
  renderRow,
  onEdit,
  onDelete,
}: {
  items: any[];
  loading: boolean;
  hasPersistedInterface: boolean;
  emptyPlaceholder: string;
  saveFirstPlaceholder: string;
  renderRow: (s: any) => React.ReactNode;
  onEdit: (s: any) => void;
  onDelete: (id: string, name: string) => void;
}) => {
  const { canUpdate, canDelete } = usePermissions();
  if (!hasPersistedInterface) {
    return (
      <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg p-3">
        {saveFirstPlaceholder}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 text-gray-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="text-sm text-gray-500 italic py-2">{emptyPlaceholder}</div>;
  }
  return (
    <div className="space-y-1">
      {items.map((s) => (
        <div
          key={s.id}
          className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-100"
        >
          {renderRow(s)}
          <div className="flex items-center space-x-1 flex-shrink-0 ml-3">
            {s._isDraft && (
              <span className="mr-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Draft
              </span>
            )}
            {canUpdate && (
            <button
              type="button"
              onClick={() => onEdit(s)}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            )}
            {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(s.id, s.subscription_name || 'subscription')}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LogicalInterfaceModal;
